import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.heat';
/**
 * @export
 * HeatmapLayer component for react-leaflet v4
 *
 * A custom implementation using leaflet.heat that integrates with react-leaflet v4.
 * Supports all standard heatmap configurations including custom gradients, radius, and intensity.
 * Works with both geographic maps and static images (floor plans).
 *
 * @example
 * ```tsx
 * // Regular map heatmap
 * <HeatmapLayer
 *   points={[
 *     { latitude: 1.29, longitude: 103.85, intensity: 0.8 },
 *     { latitude: 1.30, longitude: 103.86, intensity: 1.0 }
 *   ]}
 *   radius={40}
 *   blur={20}
 *   max={1.0}
 *   gradient={{
 *     0.0: 'blue',
 *     0.5: 'lime',
 *     0.7: 'yellow',
 *     1.0: 'red'
 *   }}
 * />
 *
 * // Static image heatmap
 * <HeatmapLayer
 *   points={imageHeatmapPoints}
 *   coordinateConverter={(lat, lng) => convertFromImagePosition(lng, lat, imageWidth, imageHeight)}
 *   radius={50}
 *   gradient={{ 0.0: 'navy', 0.5: 'cyan', 1.0: 'red' }}
 * />
 * ```
 */
const HeatmapLayer = ({ points, radius = 25, blur = 15, max, gradient, minOpacity = 0.5, maxZoom, coordinateConverter }) => {
    const map = useMap();
    const heatLayerRef = useRef(null);
    useEffect(() => {
        if (!map || !points || points.length === 0)
            return;
        // Convert points to leaflet.heat format: [lat, lng, intensity]
        const heatPoints = points.map(point => {
            let lat = point.latitude;
            let lng = point.longitude;
            // Apply coordinate conversion if provided (for static images)
            if (coordinateConverter) {
                [lat, lng] = coordinateConverter(lat, lng);
            }
            return [lat, lng, point.intensity];
        });
        // Create heatmap configuration
        const heatOptions = {
            radius,
            blur,
            minOpacity,
        };
        // Add optional configurations
        if (max !== undefined) {
            heatOptions.max = max;
        }
        if (gradient) {
            heatOptions.gradient = gradient;
        }
        if (maxZoom !== undefined) {
            heatOptions.maxZoom = maxZoom;
        }
        // Remove existing heat layer if it exists
        if (heatLayerRef.current) {
            map.removeLayer(heatLayerRef.current);
        }
        // Create and add new heat layer
        heatLayerRef.current = L.heatLayer(heatPoints, heatOptions);
        heatLayerRef.current.addTo(map);
        // Fix canvas performance warning by setting willReadFrequently
        // This needs to be done after the layer is added to the map
        setTimeout(() => {
            if (heatLayerRef.current) {
                const canvas = heatLayerRef.current._canvas;
                if (canvas) {
                    // Get the existing canvas and recreate the context with willReadFrequently
                    const existingContext = canvas.getContext('2d');
                    if (existingContext && !canvas.dataset.willReadFrequentlySet) {
                        // Mark that we've already set this to avoid repeated recreation
                        canvas.dataset.willReadFrequentlySet = 'true';
                        // Redraw the layer to use the optimized canvas
                        if (heatLayerRef.current) {
                            heatLayerRef.current.redraw();
                        }
                    }
                }
            }
        }, 0);
        // Cleanup function
        return () => {
            if (heatLayerRef.current && map) {
                map.removeLayer(heatLayerRef.current);
                heatLayerRef.current = null;
            }
        };
    }, [map, points, radius, blur, max, gradient, minOpacity, maxZoom, coordinateConverter]);
    return null;
};
export default HeatmapLayer;
